"use client";

import React, { useState } from "react";
import styles from "./Accordion.module.scss";
import RichText from "@/components/RichText";
import H2 from "@/components/H2";

interface AccordionItem {
  title: string;
  text: React.ReactNode;
  isOpen?: boolean;
}

interface AccordionProps {
  items: AccordionItem[];
  allowMultiple?: boolean;
  className?: string;
  title?: string;
}

const Accordion: React.FC<AccordionProps> = ({
  items,
  allowMultiple = false,
  className = "",
  title,
  ...props
}) => {
  const [openItems, setOpenItems] = useState<number[]>(
    items
      .map((item, index) => (item.isOpen ? index : -1))
      .filter((index) => index !== -1)
  );

  const toggleItem = (index: number) => {
    if (allowMultiple) {
      setOpenItems((prev) =>
        prev.includes(index)
          ? prev.filter((i) => i !== index)
          : [...prev, index]
      );
    } else {
      setOpenItems((prev) => (prev.includes(index) ? [] : [index]));
    }
  };

  return (
    <section
      className={`${styles.accordion} container mx-auto px-4 lg:px-24 ${className}`}
      {...props}
    >
      {title ? <H2>{title}</H2> : null}
      {items.map((item, index) => (
        <div key={index} className={styles.accordionItem}>
          <button
            className={`${styles.accordionHeader} ${
              openItems.includes(index) ? styles.open : ""
            }`}
            onClick={() => toggleItem(index)}
            aria-expanded={openItems.includes(index)}
          >
            <span className={styles.title}>{item.title}</span>
            <span
              className={`${styles.icon} ${
                openItems.includes(index) ? styles.rotate : ""
              }`}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 9L12 16L5 9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
          <div
            className={`${styles.accordionContent} ${
              openItems.includes(index) ? styles.open : ""
            }`}
          >
            <div className={styles.contentInner}>
              <RichText content={item.text} className="stack gap-6" />
            </div>
          </div>
        </div>
      ))}
    </section>
  );
};

export default Accordion;
